import Link from "next/link";
import Image from "next/image";
import React, { useState, useEffect, useRef } from "react";
import searchIcon from "@/assets/search.svg";
import { MainMenus } from "@/types/common";
// import useScrollbarWidth from "@/hooks/useScrollbarWidth";
import useStickyHeaderHeight from "@/hooks/useStickyHeaderHeight";
import { useStickyHeaderContext } from "@/context/HeaderContext";
import { twMerge } from "tailwind-merge";
import { cva } from "class-variance-authority";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import CountrySelector from "../ui/CountrySelector";
import { ChevronDown, Menu, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SearchModal from "./SearchModal";
import DropdownMenu from "../ui/Dropdown";
import GumletImage from "@/utils/GumletSrcSet";
interface HeaderProps {
  items: MainMenus;
  isScroll: boolean;
  scrollbarWidth: number;
  hostname?: string;
}

// const sections = [
//   { key: "HeaderLeft", className: "text-background text-xl font-medium" },
//   { key: "HeaderRight", className: "text-background text-xl font-normal" },
//   {
//     key: "FooterCol1",
//     className: "text-background/60 text-base font-normal font-inter",
//   },
// ];

const StickyHeader = ({ items, isScroll, scrollbarWidth, hostname }: HeaderProps) => {
  const fetchCount = useRef(0);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);
  const headerHeight = useStickyHeaderHeight(headerRef);
  const { setHeaderHeight } = useStickyHeaderContext();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [menuMobileDropdown, setMenuMobileDropdown] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { sessionUser, fetchSession, logout, sessionLoading } = useAuth();

  useEffect(() => {
    setHeaderHeight(headerHeight);
  }, [headerHeight]);

  useEffect(() => {
    const loadSession = async () => {
      if (!sessionUser?.subscriber_id && !sessionLoading && fetchCount.current < 2) {
        fetchCount.current += 1;
        await fetchSession();
      }
    };

    loadSession();
  }, [sessionUser?.subscriber_id, sessionLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    };

    const handleScroll = () => {
      setOpenDropdown(false);
    };
    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, []);

  const navVariants = cva("text-sm font-medium", {
    variants: {
      active: {
        true: "text-orange underline",
        false: "text-white md:text-gray-700",
      },
    },
    defaultVariants: { active: false },
  });

  const logoutHandler = async () => {
    await logout();
    setMenuMobileDropdown(false);
    setOpenDropdown(false);
  };

  const menuItems = items?.HeaderRight?.map((item) => ({
    label: item.label,
    href: item.href || "#",
  }));

  // console.log("Header right", items.HeaderRight)

  return (
    <>
      <header
        ref={headerRef}
        style={{
          paddingRight: scrollbarWidth > 0 ? `${scrollbarWidth}px` : "0",
          width: "100%",
          right: 0,
          left: 0,
        }}
        onMouseLeave={() => setActiveMenu(null)}
        className={`flex flex-col fixed top-0 z-50 w-full mx-auto ease-in-out 
        backdrop-blur-md bg-white border-b border-gray-200/20
        ${isScroll
            ? "translate-y-0 duration-300"
            : "-translate-y-full duration-300"
          }
      `}
      >
        <div className="w-full max-w-7xl mx-auto px-4 lg:px-8 xl:px-0">
          <div className="flex items-center py-4">
            <nav className="flex items-center gap-8 w-full">
              <Link href="/" className="flex items-center relative" prefetch={false}>
                <GumletImage
                  imageUrl={`${process.env.NEXT_PUBLIC_ASSET_URL}/images/ee121028-7432-4802-b63e-67d61336e0f2.png` || "/assets/short-logo.png"}
                  fallbackSrc="/assets/images/dummy.jpg"
                  alt="PM Logo"
                  width={68}
                  height={51}
                  className="object-contain w-[40px] md:w-[50px] lg:w-[68px]"
                  fetchPriority="high"
                />
              </Link>


              {/* Desktop Navigation */}
              <div className="hidden lg:flex justify-between w-full">
                <ul className="flex items-center space-x-8">
                  {items?.HeaderLeft?.map((item, index) => (
                    <li
                      key={`HeaderLeft-${index}`}
                      onMouseEnter={() =>
                        setActiveMenu(item.children?.length ? item.label : null)
                      }
                    >
                      <Link
                        key={item.href}
                        href={item.href || "#"}
                        className={twMerge(
                          navVariants({ active: pathname === item.href }),
                          " transition-all duration-300"
                        )}
                        onClick={() => setActiveMenu(null)}
                        prefetch={false}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>


                <div className="flex gap-3">
                  <GumletImage
                    imageUrl={"/assets/images/sticky_search.svg"}
                    fallbackSrc="/assets/images/dummy.jpg"
                    alt="Search"
                    className="cursor-pointer text-gray-700"
                    onClick={() => setIsSearchModalOpen(true)}
                    width={19}
                    height={19}
                    loading="lazy"
                  />
                  <div
                    className="text-gray-700 cursor-pointer relative"
                    onMouseEnter={() => setOpenMenu(true)}
                    onMouseLeave={() => setOpenMenu(false)}
                  >
                    {(items.HeaderRight) && <Menu className="hover:text-orange"/>}
                    <div className="absolute left-11">
                      <DropdownMenu
                        open={openMenu}
                        onClose={() => setOpenMenu(false)}
                        pathname={pathname || ""}
                        menuItems={menuItems}
                      />
                    </div>


                  </div>



                  <ul className="flex items-center space-x-6">
                    {items?.UpperNav?.filter((item => item.label != "Subscribe")).map((item, index) => (
                      <li key={`UpperNav-${index}`}>
                        <div>
                          {(item as any).label === "Login / Signup" &&
                            (sessionUser as any)?.user_email &&
                            (sessionUser as any)?.subscriber_id ? (
                            <div className="relative">
                              {" "}
                              <button
                                onClick={() => setOpenDropdown(!openDropdown)}
                                className="flex items-center gap-1 text-sm font-medium transition-all duration-300"
                               aria-label="Logout">
                                <span className="bg-orange p-1 rounded-full"><Star color="#000" size={16} /></span>
                                {/* Logout ( */}
                                {(sessionUser as any)?.first_name ||
                                  (sessionUser as any)?.last_name
                                  ? `${(sessionUser as any)?.first_name || ""} ${(sessionUser as any)?.last_name || ""
                                    }`.trim()
                                  : (sessionUser as any)?.user_email || "User"}
                                {/* ) */} <ChevronDown size={20} />
                              </button>
                              {openDropdown && (
                                <div ref={dropdownRef} className="absolute right-0 mt-2 w-52 rounded-xl shadow-lg bg-white ring-1 ring-black/5 z-50">
                                  <div className="relative">
                                    {/* Arrow */}
                                    <div className="absolute top-0 right-5 w-6 h-6 bg-white rounded-md rotate-45 -mt-2 z-[-1]" />

                                    {/* Menu items */}
                                    <ul className="py-4 px-4 text-black text-[16px] font-medium">
                                      <li>
                                        <Link onClick={() => setOpenDropdown(false)} className={`${pathname == "/my-account/profile" ? "text-orange" : ""} hover:text-orange px-4 py-2 inline-flex w-full`} href={"/my-account/profile"} prefetch={false}>
                                          My Profile
                                        </Link>{" "}
                                      </li>
                                      {/* <li className="cursor-pointer hover:text-orange">
                                    <Link onClick={() => setOpenDropdown(false)} className={`${pathname == "/my-account/subscriptions" ? "text-orange" : ""}`} href={"/my-account/subscriptions"} 
                prefetch={false}>
                                      Subscription
                                    </Link>
                                  </li> */}
                                      <li>
                                        <Link onClick={() => setOpenDropdown(false)} className={`${pathname == "/my-account/saved" ? "text-orange " : ""} hover:text-orange px-4 py-2 inline-flex w-full`} href={"/my-account/saved"} prefetch={false}>
                                          Saved
                                        </Link>
                                      </li>
                                      <li>
                                        <Link onClick={() => setOpenDropdown(false)} className={`${pathname == "/my-account/events" ? "text-orange " : ""} hover:text-orange px-4 py-2 inline-flex w-full`} href={"/my-account/events"} prefetch={false}>
                                          Events
                                        </Link>
                                      </li>
                                      <li className="px-4 cursor-pointer py-2 hover:text-orange">
                                        <span onClick={logoutHandler}>Logout</span>{" "}
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <Link
                              key={item.href}
                              href={item.href || "#"}
                              className={twMerge(
                                navVariants({ active: pathname === item.href }),
                                " transition-all duration-300"
                              )}
                              prefetch={false}
                            >
                              {item.label}
                            </Link>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>

              {/* Mobile/Tablet Controls */}
              <div className="flex items-center gap-4 ml-auto lg:hidden">
                <button className="text-black p-2" aria-label="Search">
                  <Image
                    src={searchIcon}
                    alt="Search"
                    className="text-black"
                    onClick={() => setIsSearchModalOpen(true)}
                    width={19}
                    height={19}
                    loading="lazy"
                  />
                </button>
                <button
                  className="text-gray-700 p-2"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                 aria-label="Menu">
                  <Image
                    src="/assets/images/menu.svg"
                    alt="Menu"
                    className="text-black"
                    width={28}
                    height={21}
                    loading="lazy"
                  />
                  {/* <MenuIcon size={24} /> */}
                </button>
              </div>
            </nav>
            <div className="flex items-center gap-4 lg:hidden ml-auto">
              <button className="text-background" aria-label="Search">
                <Image
                  src={"/assets/images/sticky_search.svg"}
                  alt="Search"
                  width={24}
                  height={24}
                  onClick={() => setIsSearchModalOpen(true)}
                  loading="lazy"
                />
              </button>
              <button
                className="text-white p-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
               aria-label="Menu">
                <Image
                  src="/assets/images/sticky_menu.svg"
                  className="text-white"
                  alt="Menu"
                  width={28}
                  height={21}
                  loading="lazy"
                />
              </button>
            </div>
          </div>

          {/* Child Navigation with Glassmorphism */}
          <AnimatePresence>
            {activeMenu && (
              <motion.nav
                key="submenu"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="relative w-full bg-background/80 backdrop-blur-md border-t border-[#0D121E]"
              >
                <div className="w-full max-w-7xl mx-auto px-4 py-4">
                  <ul className="flex items-center gap-6">
                    {items.HeaderLeft.find(
                      (item) => item.label === activeMenu
                    )?.children?.map((child, index) => (
                      <li key={`HeaderLeft-${index}`}>
                        <Link
                          href={child.href || "#"}
                          onClick={() => setActiveMenu(null)}
                          className="text-sm text-gray-700 hover:text-orange-500 transition-colors"
                          prefetch={false}
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.nav>
            )}
          </AnimatePresence>
          <hr className="h-[3px] bg-indigo" />
        </div>
      </header>
      <div
        className={`
          fixed inset-0 bg-indigo z-50 transition-transform duration-300 ease-in-out p-6
          ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}
          lg:hidden   
        `}
      >
        <div className="flex flex-col h-full p-4 overflow-y-auto">
          <div className="flex justify-between items-center mb-8">
            <Link href="/" className="flex items-center space-x-2" prefetch={false}>
              <GumletImage
                imageUrl={`${process.env.NEXT_PUBLIC_ASSET_URL}/images/9b10de69-1f08-4fe6-89eb-0bbdc0591cd7.png`}
                fallbackSrc="/assets/images/dummy.jpg"
                alt="People Matters Logo"
                width={70}
                height={20}
                className="w-[70px] md:w-[100px] lg:w-[120px]"
                // priority?
                fetchPriority="high"
              />
            </Link>
            <div className="flex items-center space-x-4">
              <Image
                src={searchIcon}
                alt="Search"
                width={19}
                className="cursor-pointer"
                height={19}
                onClick={() => setIsSearchModalOpen(true)}
                loading="lazy"
              />
              <button
                className="text-background p-2"
                onClick={() => setIsMobileMenuOpen(false)}
               aria-label="Menu">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu Items */}
          <div className="flex flex-col space-y-6">
            {/* {JSON.stringify(items)} */}
            {/* Show only items with href="auth/login" */}
            <div>
              <ul className="flex items-center justify-between">
                {items?.UpperNav?.filter(
                  (item) => item.label != "Subscribe"
                ).map((item, index) => (
                  <li key={`UpperNav-${index}`}>
                    <div>
                      {(item as any).label === "Login / Signup" &&
                        (sessionUser as any)?.user_email &&
                        (sessionUser as any)?.subscriber_id ? (
                        <div className="relative">
                          {" "}
                          <button
                            onClick={() =>
                              setMenuMobileDropdown(!menuMobileDropdown)
                            }
                            className="flex items-center gap-1 text-background text-sm font-medium transition-all duration-300"
                           aria-label="Menu">
                            <span className="bg-orange p-1 rounded-full">
                              <Star color="#000" size={16} />
                            </span>
                            {(sessionUser as any)?.first_name ||
                              (sessionUser as any)?.last_name
                              ? `${(sessionUser as any)?.first_name || ""} ${(sessionUser as any)?.last_name || ""
                                }`.trim()
                              : (sessionUser as any)?.user_email || "User"}
                          </button>
                        </div>
                      ) : (
                        <Link
                          key={item.href}
                          href={item.href || "#"}
                          className={twMerge(
                            navVariants({ active: pathname === item.href }),
                            " transition-all duration-300 text-xl"
                          )}
                          prefetch={false}
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
                <CountrySelector hostname={hostname} />
              </ul>

              {(sessionUser as any)?.user_email &&
                (sessionUser as any)?.subscriber_id && (
                  <ul className="pt-2 text-[16px] text-white font-medium">
                    <li className="cursor-pointer py-2 text-base opacity-60">
                      <Link onClick={() => setIsMobileMenuOpen(false)} className={`${pathname == "/my-account/profile" ? "text-orange" : ""}`} href={"/my-account/profile"} prefetch={false}>My Profile</Link>{" "}
                    </li>
                    <li className="cursor-pointer py-2 text-base opacity-60">
                      <Link onClick={() => setIsMobileMenuOpen(false)} className={`${pathname == "/my-account/subscriptions" ? "text-orange" : ""}`} href={"/my-account/subscriptions"} prefetch={false}>Subscription</Link>
                    </li>
                    <li className="cursor-pointer py-2 text-base opacity-60">
                      <Link onClick={() => setIsMobileMenuOpen(false)} className={`${pathname == "/my-account/saved" ? "text-orange" : ""}`} href={"/my-account/saved"} prefetch={false}>Saved</Link>
                    </li>
                    <li className="cursor-pointer py-2 text-base opacity-60">
                      <Link onClick={() => setIsMobileMenuOpen(false)} className={`${pathname == "/my-account/events" ? "text-orange" : ""}`} href={"/my-account/events"} prefetch={false}>Events</Link>
                    </li>
                  </ul>
                )}
            </div>
            <hr className="border-background opacity-80 my-4" />

            <ul className="flex flex-col gap-8">
              {items?.HeaderLeft?.map((item, index) => (
                <li key={`HeaderLeft-${index}`}>
                  <Link
                    key={item.href}
                    href={item.href || "#"}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={twMerge(
                      navVariants({ active: pathname === item.href }),
                      " transition-all duration-300 text-[20px] font-normal"
                    )}
                    prefetch={false}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <hr className="border-background opacity-80 my-4" />

            <ul className="flex flex-col gap-8">
              {items?.HeaderRight?.map((item, index) => (
                <li key={`HeaderRight-${index}`}>
                  <Link
                    key={item.href}
                    href={item.href || "#"}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={twMerge(
                      navVariants({ active: pathname === item.href }),
                      " transition-all duration-300 text-[20px] font-normal"
                    )}
                    prefetch={false}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="pb-20">
              <hr className="border-background opacity-80 my-4" />
              <div className="flex flex-col items-start gap-2 my-6">
                <span className="text-background text-base font-normal font-inter">
                  Find us:
                </span>
                <div className="flex items-center justify-between w-full">
                  {items?.SocialMenu?.map((item, index) => (
                    <Link
                      key={`SocialMenu-${index}`}
                      href={item.href || "#"}
                      target="_blank"
                      className="text-gray-300 hover:text-white transition-colors relative items-center"
                      prefetch={false}
                    >
                      {item.icon ? (
                        <div className="flex items-center justify-center h-6 w-6">
                          <GumletImage
                            imageUrl={item.icon}
                            fallbackSrc="https://placehold.co/100x100"
                            alt={item.label}
                            fill
                            className="object-contain"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          {item.label}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
              <hr className="border-background opacity-80" />
              {(sessionUser as any)?.user_email &&
                (sessionUser as any)?.subscriber_id && (
                  <span
                    onClick={logoutHandler}
                    className="uppercase block text-center text-orange text-base font-normal pt-8"
                  >
                    Logout
                  </span>
                )}
            </div>


            {/* if title is subcribe then stick the button to the bottom of the menu */}
            {/* stick the button to the bottom of the menu */}
            {/* {items.UpperNav.map(
                (item, index) =>
                  item.label === "Subscribe" && (
                    <Button
                      key={item.href}
                      borderVariant="none"
                      className="w-full bg-orange text-white text-lg font-medium py-4 px-4 fixed bottom-0 left-0 right-0"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Button>
                  )
              )} */}
          </div>
        </div>
      </div>
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </>
  );
};
export default StickyHeader;
